// src/components/portal/EodSettingsSection.js
import React, { useState, useEffect } from 'react';
import { tenantService, sessionService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { C } from '../../styles/theme';

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const FONT = "'Plus Jakarta Sans', sans-serif";

const inp = (extra = {}) => ({
    width: '100%', boxSizing: 'border-box',
    fontFamily: FONT, fontSize: 13, color: C.text,
    padding: '8px 12px', borderRadius: 8,
    border: `1.5px solid ${C.border}`, background: C.white,
    outline: 'none',
    ...extra,
});

const labelStyle = {
    fontFamily: FONT, fontSize: 11, fontWeight: 700,
    letterSpacing: 0.8, textTransform: 'uppercase',
    color: C.muted, display: 'block', marginBottom: 6,
};

const DEFAULT_DENOMINATIONS = [
    { value: 50,   type: 'note' },
    { value: 20,   type: 'note' },
    { value: 10,   type: 'note' },
    { value: 5,    type: 'note' },
    { value: 2,    type: 'coin' },
    { value: 1,    type: 'coin' },
    { value: 0.50, type: 'coin' },
    { value: 0.20, type: 'coin' },
    { value: 0.10, type: 'coin' },
    { value: 0.05, type: 'coin' },
    { value: 0.02, type: 'coin' },
    { value: 0.01, type: 'coin' },
];

const EodSettingsSection = ({ config, onToast, onReload }) => {
    const { getCurrency, hasFeature } = useAuth();
    const perTillLocked = !hasFeature('eod_per_till');
    const { t } = useLanguage();
    const currency = getCurrency();

    const [eodMode, setEodMode]                   = useState(config?.eod_mode || 'per_till');
    const [denominations, setDenominations]        = useState(config?.eod_denominations || DEFAULT_DENOMINATIONS);
    const [discrepancyThreshold, setDiscrepancyThreshold] = useState(config?.eod_discrepancy_threshold ?? 5);
    const [autoExpireHours, setAutoExpireHours]    = useState(config?.eod_auto_expire_hours ?? 24);
    const [saving, setSaving]                      = useState(false);
    const [hasActiveSessions, setHasActiveSessions] = useState(false);
    const [showModeWarning, setShowModeWarning]     = useState(false);
    const [pendingMode, setPendingMode]             = useState(null);

    // Check for active sessions
    useEffect(() => {
        const check = async () => {
            try {
                const sessions = await sessionService.getActiveSessions();
                setHasActiveSessions(sessions.length > 0);
            } catch (err) { console.error(err); }
        };
        check();
    }, []);

    // Sync from config prop if it changes externally
    useEffect(() => {
        if (config) {
            setEodMode(config.eod_mode || 'per_till');
            setDenominations(config.eod_denominations || DEFAULT_DENOMINATIONS);
            setDiscrepancyThreshold(config.eod_discrepancy_threshold ?? 5);
            setAutoExpireHours(config.eod_auto_expire_hours ?? 24);
        }
    }, [config]);

    const handleModeChange = (newMode) => {
        if (newMode === eodMode) return;
        if (newMode === 'per_till' && perTillLocked) return;
        if (hasActiveSessions) {
            setPendingMode(newMode);
            setShowModeWarning(true);
        } else {
            setEodMode(newMode);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        // If per_till is stored but tenant lost the feature, fall back to per_store on save
        const effectiveMode = (eodMode === 'per_till' && perTillLocked) ? 'per_store' : eodMode;
        try {
            await tenantService.updateTenantConfig({
                eod_mode: effectiveMode,
                eod_denominations: denominations,
                eod_discrepancy_threshold: parseFloat(discrepancyThreshold) || 5,
                eod_auto_expire_hours: parseInt(autoExpireHours, 10) || 24,
            });
            onToast('success', t('eod_settings_saved'));
            if (onReload) onReload();
        } catch (err) {
            console.error('Failed to save EOD settings:', err);
            onToast('error', t('failed_to_save_settings'));
        } finally {
            setSaving(false);
        }
    };

    const addDenomination = () => {
        setDenominations(prev => [...prev, { value: 0, type: 'coin' }]);
    };

    const removeDenomination = (index) => {
        setDenominations(prev => prev.filter((_, i) => i !== index));
    };

    const updateDenomination = (index, field, value) => {
        setDenominations(prev => prev.map((d, i) =>
            i === index ? { ...d, [field]: field === 'value' ? parseFloat(value) || 0 : value } : d
        ));
    };

    return (
        <div>
            {/* Section header */}
            <div style={{ marginBottom: 28 }}>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.text, fontFamily: FONT }}>
                    {t('end_of_day')}
                </h3>
                <p style={{ margin: '6px 0 0', fontSize: 13, color: C.muted, fontFamily: FONT }}>
                    {t('eod_settings_description')}
                </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 640 }}>

                {/* EOD Mode */}
                <div style={{ background: C.white, borderRadius: 10, border: `1.5px solid ${C.border}`, padding: 20 }}>
                    <label style={labelStyle}>{t('eod_mode')}</label>
                    <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                        {[
                            { value: 'per_till', label: t('per_till'), desc: t('per_till_description') },
                            { value: 'per_store', label: t('per_store'), desc: t('per_store_description') },
                        ].map(opt => {
                            const isOptionLocked = opt.value === 'per_till' && perTillLocked;
                            // Effective selection: treat per_till as per_store when locked
                            const effectiveSelected = (eodMode === 'per_till' && perTillLocked) ? 'per_store' : eodMode;
                            const isSelected = effectiveSelected === opt.value;
                            return (
                                <div
                                    key={opt.value}
                                    onClick={() => isOptionLocked ? undefined : handleModeChange(opt.value)}
                                    style={{
                                        flex: 1, padding: '14px 16px', borderRadius: 8,
                                        cursor: isOptionLocked ? 'not-allowed' : 'pointer',
                                        border: `1.5px solid ${isSelected ? C.green : C.border}`,
                                        background: isOptionLocked ? C.bg : (isSelected ? C.greenLight : C.white),
                                        opacity: isOptionLocked ? 0.5 : 1,
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: isOptionLocked ? C.muted : (isSelected ? C.green : C.text), fontFamily: FONT }}>
                                            {opt.label}
                                        </span>
                                        {isOptionLocked && (
                                            <span style={{ fontSize: 10, fontWeight: 700, color: C.green, fontFamily: FONT, background: C.greenLight, borderRadius: 4, padding: '1px 6px' }}>
                                                {t('upgrade_to_plan')} Pro
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 11, color: C.mutedLight, marginTop: 4, fontFamily: FONT }}>
                                        {opt.desc}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {showModeWarning && (
                    <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 8, padding: '14px 16px' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 6, fontFamily: FONT }}>{t('active_sessions_detected')}</div>
                        <div style={{ fontSize: 12, color: '#78350f', marginBottom: 12, fontFamily: FONT }}>
                            {t('eod_mode_change_warning')}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => { setEodMode(pendingMode); setShowModeWarning(false); setPendingMode(null); }}
                                style={{ padding: '6px 14px', borderRadius: 6, background: '#dc2626', color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>
                                {t('yes_switch')}
                            </button>
                            <button onClick={() => { setShowModeWarning(false); setPendingMode(null); }}
                                style={{ padding: '6px 14px', borderRadius: 6, background: C.white, color: C.text, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>
                                {t('cancel')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Discrepancy Threshold + Auto-expire */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div style={{ background: C.white, borderRadius: 10, border: `1.5px solid ${C.border}`, padding: 20 }}>
                        <label style={labelStyle}>{t('discrepancy_threshold')} ({currency})</label>
                        <input
                            type="number"
                            value={discrepancyThreshold}
                            onChange={e => setDiscrepancyThreshold(e.target.value)}
                            min="0"
                            step="0.01"
                            style={inp()}
                        />
                        <p style={{ fontSize: 11, color: C.mutedLight, margin: '6px 0 0', fontFamily: FONT }}>
                            {t('discrepancy_threshold_help')}
                        </p>
                    </div>
                    <div style={{ background: C.white, borderRadius: 10, border: `1.5px solid ${C.border}`, padding: 20 }}>
                        <label style={labelStyle}>{t('auto_expire_hours')}</label>
                        <input
                            type="number"
                            value={autoExpireHours}
                            onChange={e => setAutoExpireHours(e.target.value)}
                            min="1"
                            step="1"
                            style={inp()}
                        />
                        <p style={{ fontSize: 11, color: C.mutedLight, margin: '6px 0 0', fontFamily: FONT }}>
                            {t('auto_expire_hours_help')}
                        </p>
                    </div>
                </div>

                {/* Denominations */}
                <div style={{ background: C.white, borderRadius: 10, border: `1.5px solid ${C.border}`, padding: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <label style={{ ...labelStyle, marginBottom: 0 }}>{t('denominations')}</label>
                        <button
                            onClick={addDenomination}
                            style={{
                                background: C.greenLight, color: C.green, border: `1px solid ${C.greenBorder}`,
                                borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600,
                                cursor: 'pointer', fontFamily: FONT,
                            }}
                        >
                            + {t('add')}
                        </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {denominations.map((d, i) => (
                            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <input
                                    type="number"
                                    value={d.value}
                                    onChange={e => updateDenomination(i, 'value', e.target.value)}
                                    min="0"
                                    step="0.01"
                                    style={inp({ flex: 1 })}
                                    placeholder={t('value')}
                                />
                                <select
                                    value={d.type}
                                    onChange={e => updateDenomination(i, 'type', e.target.value)}
                                    style={inp({ width: 110, flex: 'none' })}
                                >
                                    <option value="note">{t('note')}</option>
                                    <option value="coin">{t('coin')}</option>
                                </select>
                                <button
                                    onClick={() => removeDenomination(i)}
                                    style={{
                                        background: C.redLight, color: C.red, border: 'none',
                                        borderRadius: 6, width: 32, height: 32, cursor: 'pointer',
                                        fontSize: 14, fontWeight: 700, flexShrink: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                    title={t('remove')}
                                >
                                    x
                                </button>
                            </div>
                        ))}
                        {denominations.length === 0 && (
                            <p style={{ fontSize: 12, color: C.mutedLight, fontFamily: FONT, textAlign: 'center', padding: 12 }}>
                                {t('no_denominations_configured')}
                            </p>
                        )}
                    </div>
                </div>

                {/* Save button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        style={{
                            background: C.green, color: C.white, border: 'none',
                            borderRadius: 8, padding: '10px 28px', fontSize: 13,
                            fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
                            fontFamily: FONT, opacity: saving ? 0.6 : 1,
                            transition: 'opacity 0.15s',
                        }}
                    >
                        {saving ? t('saving') : t('save_eod_settings')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EodSettingsSection;
